/**
 * useCampanhaForBusiness — shared cache for campaign badges across the app.
 *
 * Loads /api/v2/campanhas/active-businesses once per municipio (in-memory cache,
 * 60s TTL) and returns the campaign info for a specific business_id if any.
 *
 * Usage:
 *   const camp = useCampanhaForBusiness(business.id, municipioSlug);
 *   if (camp) <Badge style={{background: camp.cor_destaque}}>{camp.campanha_nome}</Badge>
 */
import { useEffect, useState } from 'react';
import { api } from '../services/api';

const CACHE = new Map(); // key=municipioSlug -> { ts, businesses: {id: {campanha_id,campanha_nome,cor_destaque}} }
const PENDING = new Map(); // dedupe concurrent requests
const TTL_MS = 60 * 1000;

async function fetchMap(municipioSlug) {
  const key = municipioSlug || '__all__';
  const cached = CACHE.get(key);
  if (cached && Date.now() - cached.ts < TTL_MS) return cached.businesses;
  if (PENDING.has(key)) return PENDING.get(key);
  const p = (async () => {
    try {
      const params = municipioSlug ? { municipio_slug: municipioSlug } : {};
      const r = await api.get('/v2/campanhas/active-businesses', { params });
      const businesses = r.data?.businesses || {};
      CACHE.set(key, { ts: Date.now(), businesses });
      return businesses;
    } catch {
      CACHE.set(key, { ts: Date.now(), businesses: {} });
      return {};
    } finally {
      PENDING.delete(key);
    }
  })();
  PENDING.set(key, p);
  return p;
}

export function useCampanhaForBusiness(businessId, municipioSlug) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!businessId) return undefined;
    fetchMap(municipioSlug).then((map) => {
      if (alive) setInfo(map[businessId] || null);
    });
    return () => { alive = false; };
  }, [businessId, municipioSlug]);
  return info;
}

export function invalidateCampanhaCache() {
  CACHE.clear();
}
